import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import Layout from "../../Components/layouts";
import { notify } from '../../utils';
import TextField from '@material-ui/core/TextField';
import { REQUIRED_ERROR } from '../../utils/constants';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import { UserServices } from '../../Services/UserServices';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import CircularProgress from '@material-ui/core/CircularProgress';
import VisibilityIcon from '@material-ui/icons/Visibility';
import { InputAdornment } from '@material-ui/core';
import { useHistory } from 'react-router';
import commonStyles, { pageEyebrowStyles, pageTitleTextStyles } from 'theme/commonStyles';
import { useImpersonation } from '../../hooks/useImpersonation';
import { IMPERSONATION_BLOCKED_MESSAGE } from '../../Services/impersonationSession';

const userServices = new UserServices();

const useStyles = makeStyles((theme) => ({
    pageHeader: {
        marginBottom: theme.spacing(2.5),
        textAlign: 'left',
        width: '100%',
    },
    pageEyebrow: {
        ...pageEyebrowStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageTitle: {
        ...pageTitleTextStyles,
        marginBottom: theme.spacing(0.5),
    },
    pageDescription: {
        color: '#647184',
        fontSize: '0.875rem',
        lineHeight: 1.55,
        maxWidth: 480,
    },
    formCard: {
        width: '100%',
        maxWidth: 480,
        margin: '0 auto',
        boxSizing: 'border-box',
    },
    visibilityBtn: {
        color: '#647184',
        padding: 6,
        '&:hover': {
            backgroundColor: 'rgba(61, 125, 221, 0.08)',
            color: theme.palette.primary.main,
        },
    },
    helperText: {
        color: '#647184',
        fontSize: '0.8125rem',
        lineHeight: 1.45,
        marginTop: theme.spacing(0.5),
        marginBottom: theme.spacing(1),
    },
    btn: {
        padding: '12px 28px',
        fontSize: 14,
        fontWeight: 600,
        color: 'white',
        borderRadius: 8,
        textTransform: 'none',
        boxShadow: 'none',
        minWidth: 140,
        '&:hover': {
            boxShadow: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    cancelBtn: {
        padding: '12px 28px',
        fontSize: 14,
        fontWeight: 600,
        color: '#647184',
        borderRadius: 8,
        textTransform: 'none',
        border: '1px solid #e7ebf0',
        background: '#fff',
        boxShadow: 'none',
        minWidth: 120,
        '&:hover': {
            background: '#f5f8fc',
            borderColor: '#d5dde8',
            boxShadow: 'none',
        },
        [theme.breakpoints.down('xs')]: {
            width: '100%',
        },
    },
    placeBtn: {
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        paddingTop: theme.spacing(2.5),
        marginTop: theme.spacing(2),
        borderTop: '1px solid #e7ebf0',
        [theme.breakpoints.down('xs')]: {
            flexDirection: 'column-reverse',
            gap: theme.spacing(1),
        },
    },
}));

const initFormState = { old_password: "", new_password: "", confirm_password: "" };

const ChangePassword = () => {

    const classes = useStyles();
    const commonClasses = commonStyles();
    const history = useHistory();
    const impersonation = useImpersonation();

    const [loading, setLoading] = React.useState(false)
    const [showOldText, setShowOldText] = React.useState(false)
    const [showNewText, setShowNewText] = React.useState(false)
    const [showConfirmText, setShowConfirmText] = React.useState(false)
    const [formState, setFormState] = React.useState({ ...initFormState });

    const handleInputChange = (evt) => {
        const { name, value } = evt.target;
        setFormState({ ...formState, [name]: value });
    }

    const formValidationCheck = () => {
        if (impersonation.active) {
            notify('error', IMPERSONATION_BLOCKED_MESSAGE);
            return null;
        }
        if (!formState.old_password || !formState.new_password || !formState.confirm_password) {
            notify("error", REQUIRED_ERROR);
            return null;
        }
        if (formState.new_password.length < 8 || formState.confirm_password.length < 8) {
            notify("error", 'New Password and Confirm Password must be 8 character long')
            return null
        }
        if (formState.new_password !== formState.confirm_password) {
            notify("error", 'New Password and Confirm Password must be same')
            return null
        }
        return handleFormSubmit(formState);
    }

    const handleFormSubmit = async (formState) => {
        setLoading(true)
        await userServices.changePassword(formState).then(
            data => {
                if (data && data.status) {
                    setLoading(false)
                    notify("success", data.message)
                    setFormState({ ...initFormState })
                }
            },

            error => {
                notify("error", error.response.data.message ? error.response.data.message : error.message)
                setLoading(false)
            }
        )
    }

    const handleIconClick = (type) => {
        if (type === "old") {
            setShowOldText(!showOldText)
        } else if (type === "new") {
            setShowNewText(!showNewText)
        } else {
            setShowConfirmText(!showConfirmText)
        }
    }

    const visibilityAdornment = (type, visible) => (
        <InputAdornment position="end">
            <IconButton
                className={classes.visibilityBtn}
                onClick={() => handleIconClick(type)}
                edge="end"
                size="small"
                aria-label={visible ? 'Hide password' : 'Show password'}
            >
                {visible ? <VisibilityIcon fontSize="small" /> : <VisibilityOffIcon fontSize="small" />}
            </IconButton>
        </InputAdornment>
    );

    return (
        <Layout>
            <main>
                <div className={commonClasses.contentWrapper}>
                    <div className={classes.pageHeader}>
                        <Typography className={classes.pageEyebrow}>Account</Typography>
                        <Typography className={classes.pageTitle}>Change Password</Typography>
                        <Typography className={classes.pageDescription}>
                            Update your password to keep your account secure. Use at least 8 characters.
                        </Typography>
                    </div>
                    <div className={classes.formCard}>
                        {impersonation.active ? (
                            <Typography className={classes.helperText}>
                                {IMPERSONATION_BLOCKED_MESSAGE}
                            </Typography>
                        ) : (
                        <>
                        <Typography className={classes.helperText}>
                            Enter your current password, then choose a new one.
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Current password"
                                    name="old_password"
                                    type={showOldText ? 'text' : 'password'}
                                    value={formState.old_password}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        endAdornment: visibilityAdornment('old', showOldText),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="New password"
                                    name="new_password"
                                    type={showNewText ? 'text' : 'password'}
                                    value={formState.new_password}
                                    onChange={handleInputChange}
                                    helperText="Minimum 8 characters"
                                    InputProps={{
                                        endAdornment: visibilityAdornment('new', showNewText),
                                    }}
                                />
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    variant="outlined"
                                    size="small"
                                    label="Confirm new password"
                                    name="confirm_password"
                                    type={showConfirmText ? 'text' : 'password'}
                                    value={formState.confirm_password}
                                    onChange={handleInputChange}
                                    InputProps={{
                                        endAdornment: visibilityAdornment('confirm', showConfirmText),
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <div className={classes.placeBtn}>
                            <Button
                                variant="outlined"
                                className={classes.cancelBtn}
                                onClick={() => history.goBack()}
                                disabled={loading}
                            >
                                Cancel
                            </Button>
                            <Button
                                color="primary"
                                variant="contained"
                                className={classes.btn}
                                onClick={formValidationCheck}
                                disabled={loading}
                                disableElevation
                            >
                                {loading ? <CircularProgress size={20} color="inherit" /> : 'Update password'}
                            </Button>
                        </div>
                        </>
                        )}
                    </div>
                </div>
            </main>
        </Layout>
    );
}

export default ChangePassword;
